/* ===== Landing: demo request form ===== */

import { useState } from 'react'
import { CheckCircle2, Send } from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { webSaasLeadRepository } from '@/infrastructure/repositories/leads'
import { MEMBERS_ESTIMATE_OPTIONS, ORGANIZATION_TYPE_OPTIONS, leadSchema, type LeadFormField } from './leadSchema'

interface FormState {
  fullName: string
  email: string
  phone: string
  organizationName: string
  organizationType: string
  membersEstimate: string
  message: string
  consent: boolean
  /** Honeypot: hidden from people, filled by naive bots. */
  website: string
}

const EMPTY_FORM: FormState = {
  fullName: '',
  email: '',
  phone: '',
  organizationName: '',
  organizationType: '',
  membersEstimate: '',
  message: '',
  consent: false,
  website: '',
}

const SELECT_CLASS =
  'h-11 w-full rounded-[13px] border bg-white px-3.5 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:bg-neutral-900 dark:text-neutral-100'

type FieldErrors = Partial<Record<LeadFormField, string>>

function submitErrorMessage(error: unknown): string {
  const message =
    typeof error === 'object' && error !== null && 'message' in error ? String((error as { message: unknown }).message) : ''
  if (message.includes('lead_rate_limited')) {
    return 'Recibimos varias solicitudes desde tu conexión o con este correo. Intenta nuevamente más tarde.'
  }
  if (message.includes('lead_invalid') || message.includes('lead_consent_required')) {
    return 'Revisa los datos del formulario e intenta nuevamente.'
  }
  return 'No pudimos enviar tu solicitud. Intenta nuevamente en unos minutos.'
}

export function LeadForm() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSent, setIsSent] = useState(false)

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }))
    if (field in errors) {
      setErrors((current) => ({ ...current, [field]: undefined }))
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isSubmitting) return

    const parsed = leadSchema.safeParse(form)
    if (!parsed.success) {
      const nextErrors: FieldErrors = {}
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as LeadFormField | undefined
        if (field && !nextErrors[field]) nextErrors[field] = issue.message
      }
      setErrors(nextErrors)
      const firstField = Object.keys(nextErrors)[0]
      if (firstField) document.getElementById(`lead-${firstField}`)?.focus()
      return
    }

    setErrors({})
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await webSaasLeadRepository.submit({
        ...parsed.data,
        phone: parsed.data.phone || null,
        message: parsed.data.message || null,
        website: form.website,
      })
      setForm(EMPTY_FORM)
      setIsSent(true)
    } catch (caught) {
      console.error('Error al enviar la solicitud de demo:', caught)
      setSubmitError(submitErrorMessage(caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSent) {
    return (
      <div role="status" className="flex flex-col items-center py-10 text-center">
        <div className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-200">
          <CheckCircle2 className="h-7 w-7" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Recibimos tu solicitud</h3>
        <p className="mt-2 max-w-sm text-sm text-neutral-600 dark:text-neutral-400">
          El equipo de Zetyar revisará los datos de tu organización y te escribirá al correo que indicaste.
        </p>
        <Button className="mt-6" variant="outline" onClick={() => setIsSent(false)}>
          Enviar otra solicitud
        </Button>
      </div>
    )
  }

  const fieldBorder = (field: LeadFormField) =>
    errors[field] ? 'border-danger-500' : 'border-neutral-200 dark:border-neutral-800'

  return (
    <form className="relative space-y-4" onSubmit={submit} noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          id="lead-fullName"
          label="Nombre y apellido"
          autoComplete="name"
          maxLength={120}
          value={form.fullName}
          error={errors.fullName}
          aria-invalid={Boolean(errors.fullName)}
          onChange={(event) => setField('fullName', event.target.value)}
        />
        <Input
          id="lead-email"
          label="Correo"
          type="email"
          autoComplete="email"
          maxLength={254}
          value={form.email}
          error={errors.email}
          aria-invalid={Boolean(errors.email)}
          onChange={(event) => setField('email', event.target.value)}
        />
        <Input
          id="lead-organizationName"
          label="Organización"
          autoComplete="organization"
          maxLength={160}
          value={form.organizationName}
          error={errors.organizationName}
          aria-invalid={Boolean(errors.organizationName)}
          onChange={(event) => setField('organizationName', event.target.value)}
        />
        <Input
          id="lead-phone"
          label="Teléfono (opcional)"
          type="tel"
          autoComplete="tel"
          maxLength={30}
          value={form.phone}
          error={errors.phone}
          aria-invalid={Boolean(errors.phone)}
          onChange={(event) => setField('phone', event.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="lead-organizationType" className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            Tipo de organización
          </label>
          <select
            id="lead-organizationType"
            value={form.organizationType}
            aria-invalid={Boolean(errors.organizationType)}
            aria-describedby={errors.organizationType ? 'lead-organizationType-error' : undefined}
            onChange={(event) => setField('organizationType', event.target.value)}
            className={`${SELECT_CLASS} ${fieldBorder('organizationType')}`}
          >
            <option value="">Selecciona una opción</option>
            {ORGANIZATION_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.organizationType && (
            <p id="lead-organizationType-error" className="text-xs font-semibold text-danger-600 dark:text-danger-200">
              {errors.organizationType}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="lead-membersEstimate" className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            Cantidad de miembros
          </label>
          <select
            id="lead-membersEstimate"
            value={form.membersEstimate}
            aria-invalid={Boolean(errors.membersEstimate)}
            aria-describedby={errors.membersEstimate ? 'lead-membersEstimate-error' : undefined}
            onChange={(event) => setField('membersEstimate', event.target.value)}
            className={`${SELECT_CLASS} ${fieldBorder('membersEstimate')}`}
          >
            <option value="">Selecciona un rango</option>
            {MEMBERS_ESTIMATE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.membersEstimate && (
            <p id="lead-membersEstimate-error" className="text-xs font-semibold text-danger-600 dark:text-danger-200">
              {errors.membersEstimate}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="lead-message" className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
          ¿Qué necesitan gestionar? (opcional)
        </label>
        <textarea
          id="lead-message"
          rows={4}
          maxLength={2000}
          value={form.message}
          aria-invalid={Boolean(errors.message)}
          onChange={(event) => setField('message', event.target.value)}
          className={`w-full rounded-[13px] border bg-white px-3.5 py-2.5 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:bg-neutral-900 dark:text-neutral-100 ${fieldBorder('message')}`}
        />
        {errors.message && <p className="text-xs font-semibold text-danger-600 dark:text-danger-200">{errors.message}</p>}
      </div>

      <div aria-hidden="true" className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="lead-website">Sitio web</label>
        <input
          id="lead-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(event) => setField('website', event.target.value)}
        />
      </div>

      <div>
        <label className="flex items-start gap-3 text-sm text-neutral-600 dark:text-neutral-300">
          <input
            id="lead-consent"
            type="checkbox"
            checked={form.consent}
            aria-invalid={Boolean(errors.consent)}
            aria-describedby={errors.consent ? 'lead-consent-error' : undefined}
            onChange={(event) => setField('consent', event.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-primary-600"
          />
          <span>
            Acepto que Zetyar use estos datos para responder esta solicitud. Solo se usan con ese fin y se conservan por un
            máximo de 24 meses.
          </span>
        </label>
        {errors.consent && (
          <p id="lead-consent-error" className="mt-1.5 text-xs font-semibold text-danger-600 dark:text-danger-200">
            {errors.consent}
          </p>
        )}
      </div>

      {submitError && (
        <p role="alert" className="rounded-[13px] bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-300">
          {submitError}
        </p>
      )}

      <Button type="submit" size="lg" fullWidth isLoading={isSubmitting} leftIcon={<Send className="h-4 w-4" />}>
        {isSubmitting ? 'Enviando...' : 'Solicitar demo'}
      </Button>
    </form>
  )
}
